import { useCallback, useEffect, useState } from 'react';
import type { LiveGame } from '@edh/shared';
import { isDraft } from '@edh/shared';
import { Draft } from '../components/Draft';
import { DraftPoolSheet } from '../components/DraftPool';
import { DraftMark, StandardMark } from '../components/ContestMarks';
import { EndGame } from '../components/EndGame';
import { GuildMark } from '../components/GuildMark';
import { History } from '../components/History';
import { LifeCounter, type CounterMode } from '../components/LifeCounter';
import { Lobby } from '../components/Lobby';
import { useCommentary } from '../lib/commentary';
import { ResultSheet } from '../components/ResultSheet';
import { Sheet } from '../components/Sheet';
import { StartingPlayer } from '../components/StartingPlayer';
import { TableOdds } from '../components/TableOdds';
import { RankedAvatar } from '../components/RankedAvatar';
import { claimSeat, createGame, listGames, useLiveGame } from '../lib/live';
import { useLookups, useStore } from '../lib/store';

/**
 * The Play tab.
 *
 * A game runs in the app rather than being typed in afterwards, which is the
 * only reason turn count and end-of-game life are recorded at all — they fall
 * out of a counter people were going to keep anyway.
 *
 * A game now lives on the server rather than on one phone, so everybody in the
 * guild can see it and take a chair from wherever they are sitting. The phone
 * in the middle of the table still works exactly as it did; it is just no
 * longer the only one that can.
 */
/** Where this phone records the rolls it has watched. */
const ROLLED_KEY = 'edh.rolled';

/**
 * How many of them it remembers.
 *
 * It was one, which was right while the guild could only have one table open.
 * With two pods running, a phone that watches one roll and then looks in on the
 * other table would forget the first and replay a ceremony somebody has already
 * sat through. A handful covers an evening; the oldest falls off the end.
 */
const ROLLED_KEPT = 8;

function readRolled(): string[] {
  try {
    const raw = localStorage.getItem(ROLLED_KEY);
    if (!raw) return [];
    // A single id, from a version of this screen that only had to remember one.
    const parsed: unknown = raw.startsWith('[') ? JSON.parse(raw) : [raw];
    return Array.isArray(parsed) ? parsed.filter((x): x is string => typeof x === 'string') : [];
  } catch {
    return [];
  }
}

/**
 * How long after kick-off the roll is still worth showing.
 *
 * A backstop, and the only one of the three guards that cannot be defeated by a
 * device forgetting things. Generous enough to cover a table that shuffles for
 * a minute before touching a life total, short enough that nobody is ever shown
 * a roll for a turn order the table settled a long time ago.
 */
const ROLL_WINDOW_MS = 90_000;

/**
 * Whether the game has got going.
 *
 * The roll is the ceremony *before* the first thing happens, so once anything
 * has — a life change, a turn passed, somebody knocked out — there is nothing
 * left to introduce.
 */
function underway(game: LiveGame): boolean {
  return (
    (game.turnLog?.length ?? 0) > 0 ||
    (game.log?.length ?? 0) > 0 ||
    (game.poisonLog?.length ?? 0) > 0 ||
    (game.commanderLog?.length ?? 0) > 0 ||
    (game.outLog?.length ?? 0) > 0
  );
}

/**
 * Whether this phone should play the roll at all.
 *
 * Three independent reasons not to, because one of them failing should not put
 * the ceremony back in front of somebody who has already sat down:
 *
 *   - this device has already watched it (remembered across reloads);
 *   - the game has had something happen in it;
 *   - or kick-off was a while ago, which needs nothing remembered and no log.
 *
 * The clock comparison is against a timestamp the server stamped, so it leans
 * on the two clocks being roughly in step. The window is minutes wide precisely
 * so that being roughly in step is enough.
 */
function shouldRoll(game: LiveGame, rolled: readonly string[]): boolean {
  if (rolled.includes(game.id)) return false;
  if (underway(game)) return false;
  if (game.startedAt === null) return false;
  return Date.now() - game.startedAt < ROLL_WINDOW_MS;
}

export function Play() {
  const { state, me, lastResult, clearResult, sync } = useStore();
  const { pilot, deck } = useLookups();
  /** A finished game whose result this phone has gone to fetch. */
  const [finished, setFinished] = useState<string | null>(null);
  /** And one whose result has been watched and waved away. */
  const [dismissed, setDismissed] = useState<string | null>(null);
  const [gameId, setGameId] = useState<string | null>(null);
  /** Games the guild has open. Often none, sometimes two. */
  const [open, setOpen] = useState<LiveGame[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /** The pool checklist, open between tapping Random Draft and a table existing. */
  const [picking, setPicking] = useState(false);
  /** Which format's explanation is open, from the ⓘ beside its card. */
  const [explaining, setExplaining] = useState<'standard' | 'draft' | null>(null);
  const [ending, setEnding] = useState(false);
  /**
   * The game whose roll this phone has already watched.
   *
   * On the device rather than in the document: it is a fact about what this
   * screen has shown, not about the game, and the other three phones each need
   * their own answer.
   *
   * Kept out of component state because that is a lie about what "already
   * watched" means — backing out to the Play tab and coming back in unmounts
   * this, and the ceremony would play again every time. It survives a reload
   * too, which is the same bug with a longer fuse.
   */
  const [rolled, setRolled] = useState<string[]>(readRolled);
  const [mode, setMode] = useState<CounterMode | null>(null);

  const markRolled = useCallback((id: string) => {
    setRolled((seen) => {
      if (seen.includes(id)) return seen;
      const next = [id, ...seen].slice(0, ROLLED_KEPT);
      try {
        localStorage.setItem(ROLLED_KEY, JSON.stringify(next));
      } catch {
        // Private browsing, or a full disk. Worth a repeated animation, not a crash.
      }
      return next;
    });
  }, []);

  const session = useLiveGame(gameId);

  /**
   * Look for a game on the way in.
   *
   * Slower than the in-game poll on purpose: this is a banner on a tab, not a
   * life total, and a phone sitting on the Play screen all evening should not
   * be asking every two seconds whether anything has started.
   */
  const refreshOpen = useCallback(async () => {
    try {
      const games = await listGames();
      setOpen(games);
      /*
       * Rejoin whatever is running without being asked, but only a game you are
       * actually in — walking into the room should not put you at the table.
       *
       * The newest of them, on the rare morning after where a lobby somebody
       * opened and abandoned is still sitting there with you in seat one. The
       * game you are playing is always the one that started last.
       */
      setGameId((current) => {
        if (current && games.some((g) => g.id === current)) return current;
        const mine = games
          .filter((g) => g.phase !== 'done' && g.seats.some((s) => s.pilotId === me?.id))
          .sort((a, b) => b.createdAt - a.createdAt);
        return mine[0]?.id ?? null;
      });
    } catch {
      // Offline, or not signed in yet. The cached league still renders.
    }
  }, [me?.id]);

  useEffect(() => {
    void refreshOpen();
    const timer = window.setInterval(() => {
      if (document.visibilityState === 'visible') void refreshOpen();
    }, 10_000);
    const onVisible = () => {
      if (document.visibilityState === 'visible') void refreshOpen();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [refreshOpen]);

  // The host cleared it, so there is nothing to be looking at any more.
  useEffect(() => {
    if (session.gone) {
      setGameId(null);
      setEnding(false);
      void refreshOpen();
    }
  }, [session.gone, refreshOpen]);

  /*
   * Somebody confirmed the winner. Go and find the result.
   *
   * The match is logged by the host's phone and lands in the league state,
   * which this phone syncs on its own schedule — so seeing the game turn
   * `done` is the cue to go and fetch it rather than wait. The game's own id is
   * what stops the ceremony playing twice.
   */
  useEffect(() => {
    const done = session.game;
    if (done?.phase !== 'done' || !done.matchId) return;
    if (finished === done.id) return;
    setFinished(done.id);
    setEnding(false);
    void sync();
  }, [session.game, finished, sync]);

  const game = session.game;
  const isHost = Boolean(game && me && game.hostPilotId === me.id);
  /*
   * The peanut gallery, owned here rather than inside the counter.
   *
   * It has to outlive the screen changes: the opening posts are written while
   * the table is still in the lobby arguing about the odds, and the counter
   * does not exist yet at that point. One hook at the top means the lobby, the
   * draft and the table are all looking at the same feed.
   */
  const feed = useCommentary(session);
  /*
   * A draft rolls before it deals rather than after.
   *
   * The wheel decides the ban order, so it has to happen first — but it is the
   * same ceremony meaning the same thing, and running it twice for one game
   * would be worse than running it early.
   */
  const rolling =
    (game?.phase === 'playing' || game?.phase === 'drafting') && shouldRoll(game, rolled);

  /**
   * Your own seat, for everybody.
   *
   * It used to open the host on the whole table, on the reasoning that whoever
   * started the game is the one who put a phone in the middle. Often true, and
   * it still meant the host - who is also a player - picked up their own phone
   * to a view of everybody else's life but not the one they came to change.
   * Everyone starts on their own seat now and the host flips to the table when
   * the phone is actually acting as the table.
   *
   * Somebody watching a game they are not seated in still gets the table: see
   * `layout` in LifeCounter, which will not hand out a personal view of a seat
   * that does not exist. Switchable from the menu, and only guessed once per
   * game.
   */
  useEffect(() => {
    if (!game || mode !== null) return;
    setMode('player');
  }, [game, mode]);

  useEffect(() => {
    if (!gameId) setMode(null);
  }, [gameId]);

  /*
   * Once the table is on screen, this game's roll is done with.
   *
   * Recorded here rather than only on the button that leaves the roll, because
   * the button is one of several ways to arrive at the table and the other ways
   * were leaving the record unwritten. Whatever route got somebody here, they
   * are past the ceremony.
   */
  useEffect(() => {
    if (
      (game?.phase === 'playing' || game?.phase === 'drafting') &&
      !rolling &&
      !rolled.includes(game.id)
    ) {
      markRolled(game.id);
    }
  }, [game, rolling, rolled, markRolled]);

  /*
   * The odds, once there are decks to have odds about.
   *
   * A standard table sees these in the lobby, before anybody starts. A draft
   * cannot: until the last pick lands nobody is holding anything, so the
   * forecast the guild plays for is shown at the moment it first exists.
   * Keyed by game id so it arrives once rather than on every poll.
   */
  const [oddsShown, setOddsShown] = useState<string | null>(null);
  const draftJustFinished =
    game?.phase === 'playing' && isDraft(game) && !rolling && oddsShown !== game.id;

  /**
   * Open a drafting table.
   *
   * The pool sizes the table rather than the host doing it: two decks a player
   * is the whole constraint, so the number of chairs is a fact about what is
   * physically present and not another question to answer.
   */
  async function openDraft(deckIds: string[], seats: number) {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      const created = await createGame(seats, deckIds);
      setPicking(false);
      setGameId(created.id);
      session.put(created);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'could not open a table');
    } finally {
      setBusy(false);
    }
  }

  /**
   * Join a table, and sit down while you are at it.
   *
   * Tapping a game that is open is somebody saying they are playing. Landing
   * them in a lobby to then tap a chair was one step describing what the
   * previous step already meant — and it is the step that gets forgotten,
   * which is how a table starts with somebody standing beside it.
   *
   * The lobby opens first and the chair is claimed behind it, so the tap is
   * never waiting on a request. If two people take the last seat in the same
   * second, one of them loses the version check and the poll two seconds later
   * shows them the table as it really is; there is nothing to say about it
   * that the lobby does not already say better.
   *
   * Nothing is claimed for a game already under way, or one with no room, or
   * when you are in it already. Watching is a real reason to open a table.
   */
  async function joinTable(game: LiveGame) {
    setGameId(game.id);
    if (game.phase !== 'lobby') return;
    if (game.seats.some((s) => s.pilotId === me?.id)) return;
    const seat = game.seats.findIndex((s) => !s.pilotId);
    if (seat < 0) return;
    try {
      session.put(await claimSeat(game.id, { seat }));
    } catch {
      // Somebody was a moment quicker. The lobby is already open behind this.
    }
  }

  async function openTable() {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      const created = await createGame(4);
      setGameId(created.id);
      session.put(created);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'could not open a table');
    } finally {
      setBusy(false);
    }
  }

  if (gameId && session.loading && !game) {
    return (
      <div className="empty">
        <span className="spinner" />
      </div>
    );
  }

  if (game?.phase === 'lobby') {
    return (
      <Lobby
        game={game}
        onChange={session.put}
        onLeave={() => setGameId(null)}
        onStarted={session.put}
        feed={feed}
      />
    );
  }

  /*
   * The draft, between the lobby and the table.
   *
   * The roll comes first — it decides the ban order — and then the same screen
   * carries every phone through bans and picks until the server flips the game
   * to `playing` on the last pick.
   */
  if (game?.phase === 'drafting') {
    if (rolling) {
      return (
        <StartingPlayer
          seats={game.seats.filter((s) => s.pilotId).map((s) => ({ pilotId: s.pilotId!, deckId: '' }))}
          chosen={game.startingSeat}
          draft
          onBack={() => markRolled(game.id)}
          onDone={() => markRolled(game.id)}
        />
      );
    }
    return <Draft game={game} onChange={session.put} />;
  }

  if (game?.phase === 'playing') {
    /*
     * The roll, on every phone.
     *
     * The server picked the starting seat when the host pressed Begin, so each
     * device animates towards an answer that is already decided rather than
     * deciding its own. Four phones land on the same name at the same moment
     * over a two-second poll, and nothing had to be streamed to do it.
     */
    if (rolling) {
      return (
        <StartingPlayer
          seats={game.seats.map((s) => ({ pilotId: s.pilotId!, deckId: s.deckId! }))}
          chosen={game.startingSeat}
          onBack={() => markRolled(game.id)}
          onDone={() => markRolled(game.id)}
        />
      );
    }

    return (
      <>
        <LifeCounter
          session={session}
          mode={mode ?? 'table'}
          onMode={setMode}
          isHost={isHost}
          onEnd={() => setEnding(true)}
          feed={feed}
        />
        {ending && session.view && (
          <EndGame
            game={game}
            view={session.view}
            onCancel={() => setEnding(false)}
            onFinished={() => {
              setEnding(false);
              setGameId(null);
              void refreshOpen();
            }}
          />
        )}
        {draftJustFinished && (
          <Sheet title="Polymarket" onClose={() => setOddsShown(game.id)}>
            <p className="hint" style={{ marginTop: -4 }}>
              What the table just drafted, and what the ratings make of it.
            </p>
            <TableOdds
              seats={game.seats
                .filter((s) => s.pilotId && s.deckId)
                .map((s) => ({ pilot: pilot(s.pilotId!)!, deck: deck(s.deckId!)! }))
                .filter((s) => s.pilot && s.deck)}
            />
            <button className="btn primary block" onClick={() => setOddsShown(game.id)}>
              Play
            </button>
          </Sheet>
        )}
        {lastResult && <ResultSheet result={lastResult} onClose={clearResult} feed={feed.posts} />}
      </>
    );
  }

  // A finished game is not a table anybody can join; it is a result waiting to
  // be read, and it disappears when the next one opens.
  const joinable = (open ?? []).filter(
    (g) => g.phase !== 'done' && !g.seats.some((s) => s.pilotId === me?.id),
  );

  /*
   * The result, for everybody who was not holding the phone that confirmed it.
   *
   * The host already has it as `lastResult` the moment they press Confirm, so
   * this only fires for the rest of the table — and only once the match has
   * actually arrived in the league state, which is what the sync above went to
   * fetch. Until then there is nothing to show and nothing is shown.
   */
  const watched =
    !lastResult &&
    session.game?.phase === 'done' &&
    session.game.matchId &&
    dismissed !== session.game.id
      ? state.matches.find((m) => m.id === session.game!.matchId)
      : undefined;

  return (
    <>
      {watched && (
        <ResultSheet
          result={{ match: watched, revealed: watched.revealed }}
          /* Out to the Play screen, ready for the next one — not back to a
             board whose game is over. */
          onClose={() => {
            setDismissed(session.game!.id);
            setGameId(null);
          }}
        />
      )}

      {/* The guild's own mark on the screen where you sit down to play for it.
          The tab bar says Play; this says whose. */}
      <div className="contest-head">
        <GuildMark name={state.settings.guildName} src={state.settings.guildIcon} size={72} />
        <div>
          <h2 className="contest-title">{state.settings.guildName}</h2>
          <p className="contest-sub">Guild contest</p>
        </div>
      </div>

      {/* Whatever the guild has open that you are not already sitting at,
          above the buttons that start another. Usually one card and often none;
          on a night with two pods, one each. */}
      {joinable.map((g) => (
        <JoinCard key={g.id} game={g} onJoin={() => void joinTable(g)} />
      ))}

      {error && <p className="error">{error}</p>}

      {/*
        Two modes, a mark each and a name each.

        No paragraph under either. It described the format to somebody who has
        played it forty times, on the one screen they pass through to start a
        game; the explanation lives behind the ⓘ, which is where an explanation
        belongs — available, not recited.

        Both are always live. They used to grey out whenever the guild had a
        table open, with a line underneath saying to join that one, and that
        line was the last thing standing between two pods and a second table.
      */}
      {(['standard', 'draft'] as const).map((kind) => (
        <div className="mode-row" key={kind}>
          <button
            className="mode-card"
            disabled={busy}
            onClick={() => (kind === 'standard' ? void openTable() : setPicking(true))}
          >
            {kind === 'standard' ? <StandardMark /> : <DraftMark />}
            <span className="mode-text">
              <span className="mode-title">
                {kind === 'standard' ? 'Standard Contest' : 'Random Draft'}
              </span>
            </span>
          </button>
          {/* Its own button, beside the card rather than inside it — a button
              within a button is invalid, and the tap that asks what a format is
              must never be the tap that starts one. */}
          <button
            className="mode-info"
            onClick={() => setExplaining(kind)}
            aria-label={`about ${kind === 'standard' ? 'Standard Contest' : 'Random Draft'}`}
          >
            i
          </button>
        </div>
      ))}

      {explaining && (
        <Sheet
          title={explaining === 'standard' ? 'Standard Contest' : 'Random Draft'}
          onClose={() => setExplaining(null)}
        >
          {explaining === 'standard' ? (
            <>
              <p style={{ marginTop: 0 }}>
                Open a table and everyone takes a chair from their own phone, with whatever deck
                they brought.
              </p>
              <p className="hint">
                The host can seat and equip anybody, for the people at the table who are not
                holding a phone. Roll for turn order, then keep life while you play — the turn
                count, the final life totals and who knocked whom out all fall out of the counter
                you were going to use anyway.
              </p>
            </>
          ) : (
            <>
              <p style={{ marginTop: 0 }}>
                Nobody plays the deck they brought. The host says which decks are physically on
                the table, a pool is dealt from them, and everybody bans one and then takes one.
              </p>
              <p className="hint">
                Two decks a player, and one over — so the last person to pick is still picking.
                Bans go round from whoever the wheel lands on and picks come back up the same
                order, which means <strong>whoever bans first chooses last</strong>. Going early
                buys information and costs choice; going late is the reverse.
              </p>
              <p className="hint">
                The pool decides how many can sit down: four decks seat two, eight seat four.
              </p>
            </>
          )}
        </Sheet>
      )}

      {picking && (
        <DraftPoolSheet
          busy={busy}
          onClose={() => setPicking(false)}
          onOpen={(deckIds, seats) => void openDraft(deckIds, seats)}
        />
      )}

      {/* Under the two ways to start a game, because that is what this tab is
          for — the history is what you scroll past on the way to playing, and
          what you scroll to when somebody asks who won last week. */}
      <History />

      {lastResult && <ResultSheet result={lastResult} onClose={clearResult} />}
    </>
  );
}

/** A table that is already on, and who is at it. */
function JoinCard({ game, onJoin }: { game: LiveGame; onJoin: () => void }) {
  const { pilot } = useLookups();
  const seated = game.seats.filter((s) => s.pilotId);
  const host = pilot(game.hostPilotId);

  return (
    <button className="join-card" onClick={onJoin}>
      <span className="join-live">
        <span className="join-dot" aria-hidden="true" />
        {game.phase === 'lobby' ? 'Table open' : game.phase === 'drafting' ? 'Drafting' : 'Game on'}
      </span>
      <span className="join-who">
        {seated.map((s) => (
          <RankedAvatar key={s.pilotId} pilot={pilot(s.pilotId ?? '')} size={30} />
        ))}
      </span>
      <span className="join-sub">
        {host?.name ?? 'Someone'} started it · {seated.length} seated
      </span>
      <span className="join-cta">
        {game.phase === 'lobby' ? 'Take a seat' : 'Watch'}
      </span>
    </button>
  );
}
